import flatpickr from 'flatpickr'
import cssContentFlatpickr from 'flatpickr/dist/flatpickr.min.css?inline'
import English from 'flatpickr/dist/l10n/default.js'
import { French } from 'flatpickr/dist/l10n/fr.js'
import { EventListeners, MinzeElement, Reactive } from 'minze'
import { Font, MODALS, SCREEN_WIDTHS } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'

import { AbstractSearchCore, AbstractSubscriber, ISearchConfig } from '../../../modules/search'
import {
  convertFilterFacetToTimeRange,
  convertTimeRangeToFilterFacet,
  formatUnixTimestamp,
  getTimeFromTimeRange,
  getTimeRangeFromTime,
  Time,
} from '../../../modules/time'
import { getVisibleWidth } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'datesSearch', {
  thisWeek: 'Cette semaine',
  thisWeekend: 'Ce week-end',
  nextWeek: 'Semaine prochaine',
  nextWeekend: 'Week-end prochain',
  nextThirtyDays: 'Les 30 prochains jours',
  anytime: "N'importe quand",
  when: 'Quand?',
  calendar: 'Calendrier',
  flexibleDates: 'Dates flexibles',
  empty: 'Filtez par date',
  mobileLabel: 'Date',
  label: 'Date',
  applyButton: 'Appliquer',
  chooseDatesLabel: 'Veuillez choisir une plage de dates',
})

Locale.getInstance().registerKeySet('en', 'datesSearch', {
  thisWeek: 'This week',
  thisWeekend: 'This weekend',
  nextWeek: 'Next week',
  nextWeekend: 'Next weekend',
  nextThirtyDays: 'Next 30 days',
  anytime: 'Anytime',
  when: 'When',
  calendar: 'Calendar',
  flexibleDates: 'Flexible dates',
  empty: 'Filter by date',
  mobileLabel: 'Date',
  label: 'Date',
  applyButton: 'Apply',
  chooseDatesLabel: 'Please pick a date range',
})

export interface LvcDatesSearch {
  selectedCategories: string[]
  searchMode: string
  isDesktop: boolean
}

class LvcDatesSubscriber extends AbstractSubscriber {
  private _updateCallback: (data: any) => void
  private _core: AbstractSearchCore | null = null
  private preselectedDate: Date | null = null

  constructor(groupId: string, updateCallback: (data: any) => void) {
    super(groupId)
    this._updateCallback = updateCallback
  }

  async attached(core: AbstractSearchCore): Promise<void> {
    this._core = core
  }

  detaching() {}

  public async applyDateFilters(dateFilters: number[], analyticsValue?: string): Promise<void> {
    this._core?.applyDateFilters(
      dateFilters.map(date => formatUnixTimestamp(date, 13)),
      false,
      analyticsValue
    )
    MainModule.getInstance().hideModal(MODALS.REGION_DROPDOWN)
  }

  savePreselectedDate(date: Date) {
    this.preselectedDate = date
  }

  getPreselectedDate(): Date | null {
    return this.preselectedDate
  }

  resetPreselectedDate() {
    this.preselectedDate = null
  }

  async onSearchConfigUpdate(data: any): Promise<void> {
    if (data?.dateFilters) {
      const dateFilters = data.dateFilters.map((date: number) => formatUnixTimestamp(date, 13))
      this._updateCallback(dateFilters)
    } else {
      this._updateCallback([])
    }
  }
  async onSearchTextUpdate(): Promise<void> {}

  async onFacetUpdate(): Promise<void> {}

  async onResultUpdate(): Promise<void> {}
}

export class LvcDatesSearch extends MinzeElement {
  private lvcDatesSubscriber: LvcDatesSubscriber | null = null
  private clickCb = Callback.attach(this.onButtonClick.bind(this))
  private clearCb = Callback.attach(this.onClearClick.bind(this))
  private toggleCb = Callback.attach(this.onDropdownToggle.bind(this))
  private drawerToggleCb = Callback.attach(this.onDrawerToggle.bind(this))
  private drawerClickCb = Callback.attach(this.onDrawerToggle.bind(this))
  private isOpen: boolean = false
  private _core: AbstractSearchCore | null = null

  dates: number[] = []

  categories = [
    { id: 'thisWeek', label: Locale.getKey('datesSearch.thisWeek') },
    { id: 'thisWeekend', label: Locale.getKey('datesSearch.thisWeekend') },
    { id: 'nextWeek', label: Locale.getKey('datesSearch.nextWeek') },
    { id: 'nextWeekend', label: Locale.getKey('datesSearch.nextWeekend') },
    {
      id: 'nextThirtyDays',
      label: Locale.getKey('datesSearch.nextThirtyDays'),
    },
    { id: 'anytime', label: Locale.getKey('datesSearch.anytime') },
  ]

  datePickerOptions = {
    static: true,
    inline: true,
    mode: 'range',
    dateFormat: 'Y-m-d',
    locale:
      Locale.getInstance().getLanguage() == 'fr'
        ? { ...French, firstDayOfWeek: 1 }
        : { ...English, firstDayOfWeek: 1 },
    monthSelectorType: 'static',
    firstDayOfWeek: 1,
    disable: [
      {
        // we disable all dates before today
        from: new Date(0), // A date really far in the past
        to: new Date().setDate(new Date().getDate() - 1), // Yesterday
      },
    ],
  }

  reactive: Reactive = [
    ['selectedCategories', []],
    ['searchMode', 'dynamic'],
    ['isDesktop', true],
  ]

  onDestroy() {
    Callback.detach(this.clickCb)
    Callback.detach(this.clearCb)
    Callback.detach(this.toggleCb)
    Callback.detach(this.drawerToggleCb)
    Callback.detach(this.drawerClickCb)
  }

  onButtonClick(): void {
    MainModule.getInstance().showModal(MODALS.DATES_DROPDOWN)
  }

  onClearClick(): void {
    this.clearFilter()
  }

  getAnalyticsValue = (startDate: number, endDate: number) => {
    return this.searchMode === 'dynamic'
      ? `dates flexibles:${this.getTextValue(startDate, endDate)}`
      : `calendrier:${this.getTextValue()}`
  }

  applyDates(startDate: Date, endDate: Date) {
    endDate.setHours(23, 59, 59, 999) // Set the time to the end of the day
    const endDateTimestamp = endDate.getTime()
    this.dates = [startDate.getTime(), endDateTimestamp].map(date => formatUnixTimestamp(date, 13))
    this.lvcDatesSubscriber?.applyDateFilters(
      [startDate.getTime(), endDateTimestamp],
      this.getAnalyticsValue(startDate.getTime(), endDateTimestamp)
    )
    this.lvcDatesSubscriber?.resetPreselectedDate()
  }

  onDropdownToggle(state: boolean): void {
    const firstDate = this.lvcDatesSubscriber?.getPreselectedDate()
    if (firstDate && !state) {
      const startDate = firstDate // start date
      const endDate = new Date(firstDate.getTime())
      this.applyDates(startDate, endDate)
    }
    setTimeout(() => {
      this.isOpen = state
      this.rerender()
    }, 0)
  }

  onDrawerToggle(): void {
    this.isOpen = !this.isOpen
    this.rerender()
  }

  onDrawerClick(): void {
    this.isOpen = !this.isOpen
    this.rerender()
  }

  openDatesDropdown = () => {
    MainModule.getInstance().showModal(MODALS.DATES_DROPDOWN)
  }

  openStaticDates = () => {
    this.lvcDatesSubscriber?.resetPreselectedDate()
    this.searchMode = 'static'
    this.rerender()
  }

  openDynamicDates = () => {
    this.lvcDatesSubscriber?.resetPreselectedDate()
    this.searchMode = 'dynamic'
    this.rerender()
  }

  handleDatePickerStyle = (picker: any, isDesktop: boolean) => {
    if (picker) {
      picker.calendarContainer.style.setProperty('font-size', isDesktop ? '1.3rem' : '1.2rem')
      picker.calendarContainer.style.setProperty('color', '#1E007D')
      picker.calendarContainer.style.setProperty('border', 'unset')
      picker.calendarContainer.style.setProperty('box-shadow', 'unset')
    }
  }

  setTimePreset = (dateFilters: number[]) => {
    const timeRange = convertFilterFacetToTimeRange(dateFilters, true)
    const timePreset = getTimeFromTimeRange(timeRange)

    if (timePreset) {
      if (!timeRange.anytime && timePreset === Time.ANYTIME) return
      if (this.selectedCategories?.[0] !== timePreset) {
        this.selectedCategories.splice(0, this.selectedCategories.length)
        this.selectedCategories.push(timePreset)
      }
    }
    this.rerender()
  }

  handleSearchUpdate(dateFilters: number[]) {
    this.dates = dateFilters.map(date => formatUnixTimestamp(date, 13))
    this.rerender()
  }

  clearFilter() {
    this.selectedCategories.splice(0, this.selectedCategories.length)
    this.dates = []
    this.lvcDatesSubscriber?.applyDateFilters([])
    this.rerender()
  }

  closeModal = () => {
    MainModule.getInstance().hideModal(MODALS.DATES_DROPDOWN)
  }

  onReady(): void | Promise<void> {
    this.lvcDatesSubscriber = new LvcDatesSubscriber(
      this.dataGroupId as string,
      this.handleSearchUpdate.bind(this)
    )
    MainModule.getInstance().getSearchManager().subscribe(this.lvcDatesSubscriber)
    const calendarElement = this.select('#my-calendar')
    const lvcDatesSubscriber = this.lvcDatesSubscriber
    this.setTimePreset(this.dates)
    const applyDates = (startDate: Date, endDate: Date) => {
      this.applyDates(startDate, endDate)
    }
    if (calendarElement) {
      try {
        // @ts-ignore
        const picker = flatpickr(calendarElement, {
          ...this.datePickerOptions,
          onChange: function (selectedDates: any[]) {
            if (selectedDates.length == 1) {
              lvcDatesSubscriber.savePreselectedDate(selectedDates[0])
            }
            if (selectedDates.length == 2) {
              const startDate = selectedDates[0].getTime()
              const endDate = new Date(selectedDates[1].getTime())
              applyDates(startDate, endDate)
            }
          },
        })
        this.handleDatePickerStyle(picker, this.isDesktop)
      } catch (error) {}
    }
  }

  onRender() {
    this.isDesktop = getVisibleWidth() > SCREEN_WIDTHS.DESTKOP_LARGE
    const calendarElement = this.select('#my-calendar')
    const lvcDatesSubscriber = this.lvcDatesSubscriber
    this.setTimePreset(this.dates)
    if (calendarElement) {
      const updateSelectedCategories = (dates: number[]) => {
        if (!(dates && dates.length >= 2)) return
        const startDate = new Date(dates[0])
        const endDate = new Date(dates[1])

        picker.setDate([startDate, endDate])
        if (
          this.selectedCategories?.[0] !==
          getTimeFromTimeRange(convertFilterFacetToTimeRange(dates))
        ) {
          this.selectedCategories.splice(0, this.selectedCategories.length)
          this.selectedCategories.push(getTimeFromTimeRange(convertFilterFacetToTimeRange(dates)))
          this.isOpen = false
        }
      }
      const applyDates = (startDate: Date, endDate: Date) => {
        this.applyDates(startDate, endDate)
      }
      // @ts-ignore
      const picker = flatpickr(calendarElement, {
        ...this.datePickerOptions,
        onChange: function (selectedDates: any[]) {
          if (selectedDates.length == 1) {
            lvcDatesSubscriber?.savePreselectedDate(selectedDates[0])
          }
          if (selectedDates.length == 2) {
            const startDate = selectedDates[0] // start date
            const endDate = new Date(selectedDates[1].getTime())
            applyDates(startDate, endDate)
          }
        },
      })
      updateSelectedCategories(this.dates)
      this.handleDatePickerStyle(picker, this.isDesktop)
    }
  }

  getTextValue = (startDate?: number, endDate?: number) => {
    if (this.searchMode === 'dynamic') {
      if (startDate && endDate) {
        const start = this.displayUnixDate(startDate)
        const end = this.displayUnixDate(endDate)
        return `${start} - ${end}`
      }
      if (this.dates.length === 0) {
        return
      } else if (
        this.dates.length === 2 &&
        this.displayUnixDate(this.dates[0]) !== this.displayUnixDate(this.dates[1])
      ) {
        const startDate = this.displayUnixDate(this.dates[0])
        const endDate = this.displayUnixDate(this.dates[1])
        return `${startDate} - ${endDate}`
      } else {
        return this.displayUnixDate(this.dates[0])
      }
    } else {
      // return the selected dynamic date label
      const selectedCategory = this.selectedCategories[0]
      if (!selectedCategory) {
        return
      }
      const category = this.categories.find(category => category.id === selectedCategory)
      return category?.label
    }
  }

  onStart() {}
  renderContent = () => {
    if (this.searchMode === 'dynamic') {
      return /* html */ `
        <lvc-box width="100%" display="flex" flexWrap="wrap" justifyContent="center" alignItems='center'>
         
            <lvc-box display="flex" columnGap="4px" padding="4px" border="1px solid #1E007D" borderRadius="99px">
              <lvc-button theme="dark" width="auto" fontSize="14px" fontWeight="500" padding="12px" height="46px" enable-hover-box-shadow="true">
                ${Locale.getKey('datesSearch.calendar')}
              </lvc-button>
              <lvc-button
                width="auto"
                height="46px"
                theme="transparent"
                flex="1"
                border-radius="99px"
                font-size="14px"
                font-weight="500"
                padding="12px"
                id="btn-static-dates-search"
              >
                ${Locale.getKey('datesSearch.flexibleDates')}
              </lvc-button>
            </lvc-box>


          <lvc-box mt="16px" class="calendar-container" justifyContent='center' alignItems='center'>
            <lvc-box textAlign="center" mb="16px">
              <lvc-text font-preset="small">${Locale.getKey('datesSearch.chooseDatesLabel')}</lvc-text>
            </lvc-box>
            <div type="text" id="my-calendar"></div>
          </lvc-box>
        </lvc-box>
        `
    } else if (this.searchMode === 'static') {
      return /* html */ `
        <lvc-box
          border=""
          borderRadius="16px"
          padding="0"
          ml="0"
          mr="0"
          display="flex"
          flexWrap="wrap"
          columnGap="8px"
          rowGap="8px"
          background="transparent"
          height="auto"
        >
          
            <lvc-box width="100%" display="flex" flexWrap="wrap" justifyContent="center">
              <lvc-box display="flex" columnGap="4px" padding="4px" border="1px solid #1E007D" borderRadius="99px">
                <lvc-button
                  width="auto"
                  font-size="14px"
                  font-weight="500"
                  padding="12px"
                  theme="transparent"
                  flex="1"
                  border-radius="99px"
                  height="46px"
                  id="btn-dynamic-dates-search"
                  padding="0.5rem 1rem"
                  font-weight="600"
                  
                >
                  ${Locale.getKey('datesSearch.calendar')}
                </lvc-button>
                <lvc-button theme="dark" width="auto" font-size="14px" font-weight="500" padding="12px" height="46px" enable-hover-box-shadow="true">
                  ${Locale.getKey('datesSearch.flexibleDates')}
                </lvc-button>
              </lvc-box>
     
            <lvc-box mt="6px" rowGap="15px" columnGap="8px" display="flex" flexDirection='column' width='100%' padding='14px'>
              ${this.categories
                .map(
                  category => /*html*/ `
                <lvc-filter-chip
                  theme="dark"
                  width="fit-content"
                  data-value=${category.id}
                  class="category-chip date-chip"
                   ${this.selectedCategories.includes(category.id) ? 'selected=true' : 'selected=false'}
                >
                  ${category.label}
                </lvc-filter-chip>`
                )
                .join('')}
            </lvc-box>
          </lvc-box>
        </lvc-box>
        `
    }
  }

  displayUnixDate = (unixDate: number): string => {
    const language = Locale.language
    const date = new Date(unixDate)
    const dateString = date.getDate().toString()
    const month = date.getMonth()
    let monthName = Locale.getKey('general.months.short.' + (month + 1)).substring(0, 4)
    if (language === 'fr') monthName = monthName.toLowerCase()
    return `${dateString} ${monthName}`
  }

  selectDatePreset = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement
    const presetId = target.getAttribute('data-value')

    if (presetId && this.selectedCategories.includes(presetId)) {
      return this.lvcDatesSubscriber?.applyDateFilters([])
    }
    const filter = convertTimeRangeToFilterFacet(getTimeRangeFromTime(presetId as Time), true)
    this.selectedCategories.splice(0, this.selectedCategories.length)
    this.selectedCategories.push(presetId as string)
    this.dates = filter || []
    const analyticsValue =
      this.searchMode === 'dynamic'
        ? `dates flexibles:${this.getTextValue()}`
        : `calendrier:${this.getTextValue()}`
    this.lvcDatesSubscriber?.applyDateFilters(filter || [], analyticsValue)
    this.isOpen = false
    this.rerender()
  }

  renderDesktop() {
    return /* html */ `
    
    <lvc-dropdown
      on-toggle="${this.toggleCb}"
      modal-id=${MODALS.DATES_DROPDOWN}
      flex-grow="1"
      fullscreen="true"
    >
      <lvc-search-filter-button
        class="desktop-date-search"
        slot="lvc-dropdown-trigger"
        preset="date-search"
        count="${this.dates.length > 0 || this.selectedCategories.length > 0 ? '1' : '0'}"
        ${this.isOpen ? 'selected' : ''}
        on-click="${this.clickCb}"
        on-clear="${this.clearCb}"
        data-value="${this.getTextValue()}"
      >
        ${Locale.getKey('datesSearch.label')}
      </lvc-search-filter-button>
      <div class="dropdown-content"
      slot="lvc-dropdown-content">
        ${this.renderContent()}
      </div>
    </lvc-dropdown>
   
  `
  }

  renderMobile() {
    return /* html */ `
      <lvc-search-filter-mobile
        on-toggle="${this.drawerToggleCb}"
        data-count="${this.dates.length > 0 ? '1' : '0'}"
        title="${Locale.getKey('datesSearch.label')}"
        ${this.isOpen ? `open="true"` : ''}
      >
        <lvc-search-filter-button
          slot="lvc-drawer-trigger"
          preset="date-search-mobile"
          count="${this.dates.length > 0 ? '1' : '0'}"
          on-click="${this.drawerClickCb}"
          on-clear="${this.clearCb}"
          data-value="${this.getTextValue()}"
        >
          ${Locale.getKey('datesSearch.label')}
        </lvc-search-filter-button>
        <div class="drawer-content"
        slot="lvc-drawer-content">
          ${this.renderContent()}
        </div>
      </lvc-search-filter-mobile>
    `
  }

  html = () => {
    if (getVisibleWidth() < SCREEN_WIDTHS.DESKTOP_MEDIUM) {
      return this.renderMobile()
    }

    return this.renderDesktop()
  }
  eventListeners: EventListeners = [
    ['lvc-clear-filter-button', 'click', this.clearFilter.bind(this)],
    [
      '#btn-dates-search',
      'click',
      event => {
        const targetElement = event.target as HTMLElement
        if (targetElement.tagName !== 'LVC-CLEAR-FILTER-BUTTON') this.openDatesDropdown()
      },
    ],
    ['#btn-static-dates-search', 'click', this.openStaticDates],
    ['#btn-dynamic-dates-search', 'click', this.openDynamicDates],
    ['.date-chip', 'click', this.selectDatePreset],
    ['lvc-back-icon', 'click', this.closeModal],
    [window, 'lvc:clear-search', this.clearFilter.bind(this)],
  ]

  css = () => `
    ${cssContentFlatpickr}

    .flatpickr-months .flatpickr-month {
      display: flex;
      align-items: center;
    }

    .flatpickr-months .flatpickr-prev-month, .flatpickr-months .flatpickr-next-month {
      padding: 0 10px;
      display: flex;
      align-items: center;
    }

    .flatpickr-current-month {
      width: 100%;
      position: static;
      padding: 0;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: ${Font.weight.bold};
    }

    .flatpickr-current-month span.cur-month:hover, .numInputWrapper:hover {
      background: none;
    }

    .flatpickr-current-month span.cur-month, .flatpickr-current-month input.cur-year {
      font-weight: ${Font.weight.bold};
      font-size: 17px;
      line-height: 120%;
      color: #1E007D;
      text-transform: capitalize;
    }

    .flatpickr-current-month .numInputWrapper {
      width: 50px;
      display: flex;
      align-items: center;
    }

    .numInputWrapper span.arrowUp, .numInputWrapper span.arrowDown {
      display: none;
    }

    .dayContainer, .flatpickr-days, .flatpickr-calendar {
      width: 336px;
    }

    .flatpickr-days {
      margin-top: 12px;
    }

    .dayContainer {
      max-width: 336px;
      min-width: 336px;
      row-gap: 4px;
      height: calc(5 * 48px + 16px);
      overflow: hidden;
    }

    .flatpickr-day {
      border: none;
      margin: 0;
      max-width: none;
      width: 48px;
      height: 48px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 16px;
      color: #1E007D;
    }

    .flatpickr-day:hover, .flatpickr-day.today:hover {
      background: #BFF0FF;
      border-radius: 50%;
      color: #1E007D;
    }

    .flatpickr-day.today.selected:hover, .flatpickr-day.today.endRange:hover, .flatpickr-day.today.startRange:hover {
      color: #FFFFFF;
    }

    .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n+1)),
    .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n+1)),
    .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n+1)) {
      box-shadow: none;
    }

    .flatpickr-day.inRange, .flatpickr-day.today.inRange {
      background: #BFF0FF;
      box-shadow: none;
    }

    .flatpickr-day.inRange:hover {
      border-radius: 0;
    }

    .rangeMode .flatpickr-day {
      margin-top: 0;
    }

    .flatpickr-day.selected {
      background: #1E007D;
    }

    .flatpickr-day.startRange, .flatpickr-day.endRange, .flatpickr-day.startRange:hover, .flatpickr-day.endRange:hover {
      background: #1E007D;
    }

    .flatpickr-day.selected,
    .flatpickr-day.startRange,
    .flatpickr-day.endRange,
    .flatpickr-day.selected.inRange,
    .flatpickr-day.startRange.inRange,
    .flatpickr-day.endRange.inRange,
    .flatpickr-day.selected:focus,
    .flatpickr-day.startRange:focus,
    .flatpickr-day.endRange:focus,
    .flatpickr-day.selected:hover,
    .flatpickr-day.startRange:hover,
    .flatpickr-day.endRange:hover,
    .flatpickr-day.selected.prevMonthDay,
    .flatpickr-day.startRange.prevMonthDay,
    .flatpickr-day.endRange.prevMonthDay,
    .flatpickr-day.selected.nextMonthDay,
    .flatpickr-day.startRange.nextMonthDay,
    .flatpickr-day.endRange.nextMonthDay {
      background: #1E007D;
    }

    .flatpickr-day.selected, .flatpickr-day.startRange {
      border-color: #1E007D;
    }

    .flatpickr-day.selected.startRange, .flatpickr-day.startRange.startRange, .flatpickr-day.endRange.startRange {
      border-radius: 50%;
    }
    
    .flatpickr-day.endRange:after, .flatpickr-day.inRange:before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: -1px;
      width: 100%;
      z-index: -1;
      background: #BFF0FF;
      transform: translateX(-50%);
    }
    
    .flatpickr-day.startRange.endRange:after {
      content: none;
    }
    
    .flatpickr-day.inRange:before {
      left: 0;
      width: 50%;
      transform: translateX(-100%)
    }
    
    .flatpickr-day.prevMonthDay.endRange:after,
    .flatpickr-day.nextMonthDay.endRange:after,
    .flatpickr-day.prevMonthDay.inRange:before,
    .flatpickr-day.nextMonthDay.inRange:before {
      background: #E6E6E6;
    }

    .flatpickr-day.selected.endRange, .flatpickr-day.startRange.endRange, .flatpickr-day.endRange.endRange {
      border-radius: 50%;
    }

    .flatpickr-day.flatpickr-disabled,
    .flatpickr-day.flatpickr-disabled:hover,
    .flatpickr-day.prevMonthDay,
    .flatpickr-day.nextMonthDay,
    .flatpickr-day.notAllowed,
    .flatpickr-day.notAllowed.prevMonthDay,
    .flatpickr-day.notAllowed.nextMonthDay {
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 16px;
      color: rgba(30, 0, 125, 0.3);
    }

    .flatpickr-weekdays {
      border-bottom: 1px solid rgba(30, 0, 125, 0.3);
      height: auto;
      padding: 16px 0 12px 0;
      font-weight: ${Font.weight.bold};
    }

    span.flatpickr-weekday {
      font-weight: ${Font.weight.bold};
      font-size: 12px;
      line-height: 120%;
      color: #1E007D;
    }

    h4 {
      margin: 0 0 4px 0;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      opacity: 0.3;
    }


    .drawer-content .flatpickr-calendar {
      background: transparent;
    }

    .drawer-content {
      position: relative;
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
    }

    .drawer-content > * {
      max-width: 340px;
    }

    @media screen and (max-width: 730px) {
      .calendar-container {
        max-height: calc(100vh - 72px - 73px - 84.5px - 10px);
        overflow-y: scroll;
        padding-bottom: 10px;
      }
    }
  `
}
